import React, { useState, useCallback, useEffect } from "react";
import {
    DialogContent,
    DialogActions,
    Button,
    Typography,
    DialogTitle,
    Dialog,
    Box,
    Chip,
    IconButton,
    Tooltip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    CircularProgress,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Grid,
    Card,
    CardContent,
    useMediaQuery,
    alpha,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import LinkIcon from "@mui/icons-material/Link";
import SendIcon from "@mui/icons-material/Send";
import KeyIcon from "@mui/icons-material/Key";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTheme } from "@mui/material/styles";
import { grey } from "@mui/material/colors";
import { useAtom } from "jotai";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import {
    openViewWebhookModalAtom,
    selectedWebhookAtom,
    WEBHOOK_EVENT_TYPE_LABELS
} from "../../atoms/webhooks";
import {
    mbxUserAtom,
    conformationDialogOpenAtom,
    conformationDialogContentAtom,
    conformationDialogTitleAtom,
    conformationDialogSubmitButtonAtom,
    confirmationFunctionAtom
} from "../../atoms/atoms";
import {
    getJsonApi,
    putApiWithStatusCode,
    delApiWithStatus,
    postApiWithStatusCode
} from "../../modules/AmplifyServices";
import { userPageSnackbarAtom } from "../../atoms/usersTable";

const TIME_FILTER_OPTIONS = [
    { value: "1h", label: "Last hour" },
    { value: "7d", label: "Last 7 days" },
    { value: "30d", label: "Last 30 days" }
];

const LOGS_PER_PAGE = 50;
const LOGS_TABLE_ROWS_PER_PAGE = 10;

const ViewWebhookModal = ({ refetch }) => {
    const theme = useTheme();
    const [openViewWebhookModal, setOpenViewWebhookModal] = useAtom(
        openViewWebhookModalAtom
    );
    const [selectedWebhook, setSelectedWebhook] = useAtom(selectedWebhookAtom);
    const [, setWebhookPageSnackbar] = useAtom(userPageSnackbarAtom);
    const [mbxUser] = useAtom(mbxUserAtom);
    const [, setConfirmationDialogOpen] = useAtom(conformationDialogOpenAtom);
    const [, setConfirmationDialogContent] = useAtom(
        conformationDialogContentAtom
    );
    const [, setConfirmationDialogTitle] = useAtom(conformationDialogTitleAtom);
    const [, setConfirmationDialogSubmitButton] = useAtom(
        conformationDialogSubmitButtonAtom
    );
    const [, setConfirmationFunction] = useAtom(confirmationFunctionAtom);
    const [showSecret, setShowSecret] = useState(false);
    const [secretKey, setSecretKey] = useState(null);
    const [isLoadingSecret, setIsLoadingSecret] = useState(false);
    const [isRotatingSignature, setIsRotatingSignature] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isTesting, setIsTesting] = useState(false);
    const [isRetrying, setIsRetrying] = useState(false);
    const [testCooldown, setTestCooldown] = useState(0);
    const [retryCooldown, setRetryCooldown] = useState(0);

    // Logs state
    const [timeFilter, setTimeFilter] = useState("7d");
    const [logs, setLogs] = useState([]);
    const [nextToken, setNextToken] = useState(null);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);

    // Pagination state for logs table
    const [logsPage, setLogsPage] = useState(0);
    const [logsRowsPerPage, setLogsRowsPerPage] = useState(
        LOGS_TABLE_ROWS_PER_PAGE
    );

    const isDesktop = useMediaQuery(theme.breakpoints.up("md"), {
        defaultMatches: true
    });

    const { companyId } = mbxUser;

    const fetchLogs = useCallback(
        async (token = null) => {
            const queryParams = {
                companyId,
                limit: LOGS_PER_PAGE
            };

            if (timeFilter) {
                queryParams.timeFilter = timeFilter;
            }

            if (token) {
                queryParams.nextToken = token;
            }

            const response = await getJsonApi(
                "MB-rest-api",
                `/v1/mb/webhooks/${selectedWebhook.id}/logs`,
                { queryParams }
            );

            return {
                logs: response?.logs || [],
                nextToken: response?.nextToken || null
            };
        },
        [companyId, selectedWebhook?.id, timeFilter]
    );

    const {
        isLoading: isLoadingLogs,
        refetch: refetchLogs,
        isFetching
    } = useQuery({
        queryKey: ["webhook-logs", selectedWebhook?.id, timeFilter],
        queryFn: async () => {
            try {
                const result = await fetchLogs();
                setLogs(result.logs);
                setNextToken(result.nextToken);
                setLogsPage(0); // Reset pagination when fetching new logs
                return result;
            } catch (e) {
                console.error("Error fetching webhook logs:", e);
                setLogs([]);
                setNextToken(null);
                return { logs: [], nextToken: null };
            }
        },
        enabled: !!selectedWebhook?.id && !!companyId && openViewWebhookModal
    });

    const handleLoadMore = async () => {
        if (!nextToken || isLoadingMore) return;

        setIsLoadingMore(true);
        try {
            const result = await fetchLogs(nextToken);
            setLogs((prev) => [...prev, ...result.logs]);
            setNextToken(result.nextToken);
        } catch (e) {
            console.error("Error loading more logs:", e);
        } finally {
            setIsLoadingMore(false);
        }
    };

    const handleTimeFilterChange = (event) => {
        setTimeFilter(event.target.value);
        setLogs([]);
        setNextToken(null);
        setSelectedLog(null);
        setLogsPage(0);
    };

    const handleRefreshLogs = () => {
        setLogs([]);
        setNextToken(null);
        setSelectedLog(null);
        setLogsPage(0);
        refetchLogs();
    };

    const handleClose = () => {
        setShowSecret(false);
        setSecretKey(null);
        setTimeFilter("7d");
        setLogs([]);
        setNextToken(null);
        setSelectedLog(null);
        setLogsPage(0);
        setOpenViewWebhookModal(false);
        setSelectedWebhook(null);
        setConfirmationDialogOpen(false);
    };

    const handleCopy = (text, label) => {
        navigator.clipboard.writeText(text);
        setWebhookPageSnackbar({
            open: true,
            message: `${label} copied to clipboard`,
            severity: "success"
        });
    };

    const handleRevealSignature = async () => {
        if (showSecret) {
            // Just hide if already showing
            setShowSecret(false);
            return;
        }

        if (secretKey) {
            // Already fetched, just show it
            setShowSecret(true);
            return;
        }

        // Fetch the full webhook to get the secret key
        setIsLoadingSecret(true);
        try {
            const response = await getJsonApi(
                "MB-rest-api",
                `/v1/mb/webhooks/${selectedWebhook.id}`,
                {
                    queryParams: { companyId }
                }
            );

            if (response?.webhook?.secretKey) {
                setSecretKey(response.webhook.secretKey);
                setShowSecret(true);
            } else {
                setWebhookPageSnackbar({
                    open: true,
                    message: "No signature key found for this webhook",
                    severity: "warning"
                });
            }
        } catch (error) {
            console.error("Error fetching webhook signature:", error);
            setWebhookPageSnackbar({
                open: true,
                message: "Failed to retrieve signature",
                severity: "error"
            });
        } finally {
            setIsLoadingSecret(false);
        }
    };

    const executeRotateSignature = async () => {
        if (!selectedWebhook) return;

        setIsRotatingSignature(true);
        try {
            const { statusCode, body } = await postApiWithStatusCode(
                "MB-rest-api",
                `/v1/mb/webhooks/${selectedWebhook.id}/rotate-signature`,
                {
                    body: { companyId }
                }
            );

            if (statusCode >= 200 && statusCode < 300) {
                // Update the local secret key with the new one
                setSecretKey(body.webhook.secretKey);
                setShowSecret(true);
                setWebhookPageSnackbar({
                    open: true,
                    message: "Signature rotated successfully",
                    severity: "success"
                });
            } else {
                throw new Error(body?.message || "Failed to rotate signature");
            }
        } catch (error) {
            console.error("Error rotating signature:", error);
            setWebhookPageSnackbar({
                open: true,
                message: error.message || "Failed to rotate signature",
                severity: "error"
            });
        } finally {
            setIsRotatingSignature(false);
            setConfirmationDialogOpen(false);
        }
    };

    const handleRotateSignature = () => {
        if (!selectedWebhook) return;

        setConfirmationFunction(() => executeRotateSignature());
        setConfirmationDialogContent(
            <Box>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                    Are you sure you want to rotate this webhook's signature?
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        color: "error.main",
                        fontWeight: 500
                    }}
                >
                    Warning: The current signature will be permanently replaced
                    and can never be recovered. Any integrations using the old
                    signature will stop working.
                </Typography>
            </Box>
        );
        setConfirmationDialogTitle("Rotate Signature");
        setConfirmationDialogSubmitButton("Rotate");
        setConfirmationDialogOpen(true);
    };

    const handleToggleStatus = async () => {
        if (!selectedWebhook) return;

        setIsUpdating(true);
        try {
            const newStatus =
                selectedWebhook.status === "active" ? "inactive" : "active";

            const { statusCode } = await putApiWithStatusCode(
                "MB-rest-api",
                `/v1/mb/webhooks/${selectedWebhook.id}`,
                {
                    body: {
                        companyId,
                        status: newStatus
                    }
                }
            );

            if (statusCode >= 200 && statusCode < 300) {
                setSelectedWebhook({ ...selectedWebhook, status: newStatus });
                setWebhookPageSnackbar({
                    open: true,
                    message: `Webhook ${newStatus === "active" ? "published" : "unpublished"} successfully`,
                    severity: "success"
                });
                if (refetch) refetch();
            }
        } catch (error) {
            console.error("Error updating webhook:", error);
            setWebhookPageSnackbar({
                open: true,
                message: "Failed to update webhook",
                severity: "error"
            });
        } finally {
            setIsUpdating(false);
        }
    };

    const executeDelete = async () => {
        if (!selectedWebhook) return;

        setIsDeleting(true);
        try {
            const { statusCode } = await delApiWithStatus(
                "MB-rest-api",
                `/v1/mb/webhooks/${selectedWebhook.id}`,
                {
                    queryParams: {
                        companyId
                    }
                }
            );

            if (statusCode >= 200 && statusCode < 300) {
                setWebhookPageSnackbar({
                    open: true,
                    message: "Webhook deleted successfully",
                    severity: "success"
                });
                handleClose();
                if (refetch) refetch();
            }
        } catch (error) {
            console.error("Error deleting webhook:", error);
            setWebhookPageSnackbar({
                open: true,
                message: "Failed to delete webhook",
                severity: "error"
            });
        } finally {
            setIsDeleting(false);
        }
    };

    const handleDelete = () => {
        if (!selectedWebhook) return;

        setConfirmationFunction(() => executeDelete());
        setConfirmationDialogContent(
            <Box>
                <Typography variant="body2">
                    Are you sure you want to delete this webhook? This cannot be
                    undone.
                </Typography>
            </Box>
        );
        setConfirmationDialogTitle("Delete webhook");
        setConfirmationDialogSubmitButton("Delete");
        setConfirmationDialogOpen(true);
    };

    const handleSelectLog = (log) => {
        setSelectedLog(selectedLog?.id === log.id ? null : log);
    };

    const handleSendTestEvent = async () => {
        if (!selectedWebhook) return;

        setIsTesting(true);
        try {
            const { statusCode, body } = await postApiWithStatusCode(
                "MB-rest-api",
                `/v1/mb/webhooks/${selectedWebhook.id}/test`,
                {
                    body: {
                        companyId
                    }
                }
            );

            if (statusCode >= 200 && statusCode < 300) {
                // Check the actual delivery result from the response
                if (body?.success) {
                    setWebhookPageSnackbar({
                        open: true,
                        message: `Test event delivered successfully (HTTP ${body.httpStatus})`,
                        severity: "success"
                    });
                } else {
                    setWebhookPageSnackbar({
                        open: true,
                        message: `Test event delivery failed: ${body?.errorMessage || "Unknown error"}`,
                        severity: "warning"
                    });
                }
                // Refresh logs to show the test event
                setTimeout(() => {
                    handleRefreshLogs();
                }, 1000);
            } else {
                throw new Error(body?.message || "Failed to send test event");
            }
        } catch (error) {
            console.error("Error sending test event:", error);
            setWebhookPageSnackbar({
                open: true,
                message: error.message || "Failed to send test event",
                severity: "error"
            });
        } finally {
            setIsTesting(false);
            // Start 10 second cooldown
            setTestCooldown(10);
        }
    };

    const handleRetryDelivery = async (log) => {
        if (!selectedWebhook || !log) return;

        setIsRetrying(true);
        try {
            const { statusCode, body } = await postApiWithStatusCode(
                "MB-rest-api",
                `/v1/mb/webhooks/${selectedWebhook.id}/retry`,
                {
                    body: {
                        companyId,
                        payload: log.requestPayload,
                        eventType: log.eventType
                    }
                }
            );

            if (statusCode >= 200 && statusCode < 300) {
                if (body?.success) {
                    setWebhookPageSnackbar({
                        open: true,
                        message: `Retry delivered successfully (HTTP ${body.httpStatus})`,
                        severity: "success"
                    });
                } else {
                    setWebhookPageSnackbar({
                        open: true,
                        message: `Retry delivery failed: ${body?.errorMessage || "Unknown error"}`,
                        severity: "warning"
                    });
                }
                // Refresh logs to show the retry
                setTimeout(() => {
                    handleRefreshLogs();
                }, 1000);
            } else {
                throw new Error(body?.message || "Failed to retry delivery");
            }
        } catch (error) {
            console.error("Error retrying delivery:", error);
            setWebhookPageSnackbar({
                open: true,
                message: error.message || "Failed to retry delivery",
                severity: "error"
            });
        } finally {
            setIsRetrying(false);
            // Start 10 second cooldown
            setRetryCooldown(10);
        }
    };

    // Cooldown timer effects
    useEffect(() => {
        if (testCooldown > 0) {
            const timer = setTimeout(
                () => setTestCooldown(testCooldown - 1),
                1000
            );
            return () => clearTimeout(timer);
        }
    }, [testCooldown]);

    useEffect(() => {
        if (retryCooldown > 0) {
            const timer = setTimeout(
                () => setRetryCooldown(retryCooldown - 1),
                1000
            );
            return () => clearTimeout(timer);
        }
    }, [retryCooldown]);

    // Pagination handlers for logs table
    const handleChangeLogsPage = (event, newPage) => {
        setLogsPage(newPage);
        // If we're approaching the end of loaded logs and there's more to load
        if (
            newPage * logsRowsPerPage >= logs.length - logsRowsPerPage &&
            nextToken
        ) {
            handleLoadMore();
        }
    };

    const handleChangeLogsRowsPerPage = (event) => {
        setLogsRowsPerPage(parseInt(event.target.value, 10));
        setLogsPage(0);
    };

    if (!selectedWebhook) return null;

    const isActive = selectedWebhook.status === "active";
    const hasSelectedLog = !!selectedLog;

    // Get paginated logs
    const paginatedLogs = logs.slice(
        logsPage * logsRowsPerPage,
        logsPage * logsRowsPerPage + logsRowsPerPage
    );

    return (
        <Dialog
            open={openViewWebhookModal}
            onClose={handleClose}
            maxWidth={hasSelectedLog ? "xl" : "md"}
            fullWidth
            fullScreen={!isDesktop}
            PaperProps={{
                sx: {
                    transition: "max-width 0.3s ease-in-out",
                    maxHeight: "90vh"
                }
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    padding: "36px",
                    paddingBottom: "8px",
                    fontSize: "24px"
                }}
            >
                <Box>
                    <Typography sx={{ fontSize: "24px", lineHeight: 1.1 }}>
                        {selectedWebhook.name}
                    </Typography>
                </Box>
                <Button
                    variant={isActive ? "outlined" : "contained"}
                    color={isActive ? "inherit" : "primary"}
                    onClick={handleToggleStatus}
                    disabled={isUpdating}
                    sx={{
                        textTransform: "none",
                        minWidth: "100px",
                        fontWeight: 600,
                        ...(isActive && {
                            borderColor: grey[300],
                            color: "text.secondary",
                            "&:hover": {
                                borderColor: grey[400],
                                backgroundColor: grey[50]
                            }
                        }),
                        ...(!isActive && {
                            animation: "subtlePulse 2s ease-in-out infinite",
                            "@keyframes subtlePulse": {
                                "0%, 100%": {
                                    boxShadow: `0 0 0 0 ${alpha(theme.palette.primary.main, 0.4)}`
                                },
                                "50%": {
                                    boxShadow: `0 0 0 8px ${alpha(theme.palette.primary.main, 0)}`
                                }
                            }
                        })
                    }}
                >
                    {isUpdating ? "..." : isActive ? "Unpublish" : "Publish"}
                </Button>
            </DialogTitle>

            <DialogContent
                sx={{
                    padding: "36px",
                    pt: "16px",
                    pb: "16px",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden"
                }}
            >
                {/* Status Note */}
                {!isActive && (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2, fontStyle: "italic" }}
                    >
                        This webhook is unpublished and will not receive live
                        events.
                    </Typography>
                )}
                <Box
                    sx={{
                        display: "flex",
                        gap: 3,
                        minHeight: 400,
                        flex: 1,
                        overflow: "hidden"
                    }}
                >
                    {/* Main Content */}
                    <Box
                        sx={{
                            flex: hasSelectedLog ? "0 0 55%" : "1 1 100%",
                            transition: "flex 0.3s ease-in-out",
                            overflow: "auto"
                        }}
                    >
                        <Grid container spacing={2.25}>
                            {/* Description Accordion */}
                            {selectedWebhook.description && (
                                <Grid item xs={12}>
                                    <Accordion
                                        defaultExpanded
                                        disableGutters
                                        elevation={0}
                                        sx={{
                                            border: "none",
                                            "&:before": {
                                                display: "none"
                                            },
                                            backgroundColor: "transparent"
                                        }}
                                    >
                                        <AccordionSummary
                                            expandIcon={
                                                <ExpandMoreIcon
                                                    sx={{
                                                        fontSize: 20,
                                                        color: "text.secondary"
                                                    }}
                                                />
                                            }
                                            sx={{
                                                minHeight: "unset",
                                                padding: 0,
                                                "& .MuiAccordionSummary-content":
                                                    {
                                                        margin: 0
                                                    }
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.5px",
                                                    fontWeight: 500
                                                }}
                                            >
                                                Description
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails
                                            sx={{
                                                padding: 0,
                                                pt: 1
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ lineHeight: 1.6 }}
                                            >
                                                {selectedWebhook.description}
                                            </Typography>
                                        </AccordionDetails>
                                    </Accordion>
                                </Grid>
                            )}

                            {/* Configuration Card */}
                            <Grid item xs={12}>
                                <Card
                                    variant="outlined"
                                    sx={{ borderColor: grey[300] }}
                                >
                                    <CardContent sx={{ pb: "16px !important" }}>
                                        {/* Event Type */}
                                        <Box sx={{ mb: 2.5 }}>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.5px",
                                                    fontWeight: 500,
                                                    display: "block",
                                                    mb: 0.75
                                                }}
                                            >
                                                Event Type
                                            </Typography>
                                            <Chip
                                                label={
                                                    WEBHOOK_EVENT_TYPE_LABELS[
                                                        selectedWebhook
                                                            .eventType
                                                    ] ||
                                                    selectedWebhook.eventType
                                                }
                                                size="small"
                                                sx={{
                                                    backgroundColor: alpha(
                                                        theme.palette.primary
                                                            .main,
                                                        0.08
                                                    ),
                                                    color: theme.palette.primary
                                                        .dark,
                                                    fontWeight: 500
                                                }}
                                            />
                                        </Box>

                                        {/* URL */}
                                        <Box sx={{ mb: 2.5 }}>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.5px",
                                                    fontWeight: 500,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 0.5,
                                                    mb: 0.75
                                                }}
                                            >
                                                {/* <LinkIcon
                                                    sx={{ fontSize: 14 }}
                                                /> */}
                                                Endpoint URL
                                            </Typography>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1,
                                                    backgroundColor: grey[50],
                                                    borderRadius: 1,
                                                    px: 1.5,
                                                    py: 1
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontFamily: "monospace",
                                                        flex: 1,
                                                        wordBreak: "break-all",
                                                        fontSize: "13px"
                                                    }}
                                                >
                                                    {selectedWebhook.url}
                                                </Typography>
                                                <Tooltip title="Copy URL">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            handleCopy(
                                                                selectedWebhook.url,
                                                                "URL"
                                                            )
                                                        }
                                                    >
                                                        <ContentCopyIcon
                                                            sx={{
                                                                fontSize: 16
                                                            }}
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>

                                        {/* Secret Key */}
                                        {/*<Box
                                            sx={{
                                                mb:
                                                    selectedWebhook.headers &&
                                                    Object.keys(
                                                        selectedWebhook.headers
                                                    ).length > 0
                                                        ? 2.5
                                                        : 0
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.5px",
                                                    fontWeight: 500,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 0.5,
                                                    mb: 0.75
                                                }}
                                            >
                                                 <KeyIcon
                                                    sx={{ fontSize: 14 }}
                                                />
                                                Secret Key
                                            </Typography>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1,
                                                    backgroundColor: grey[50],
                                                    borderRadius: 1,
                                                    px: 1.5,
                                                    py: 1
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontFamily: "monospace",
                                                        flex: 1,
                                                        wordBreak: "break-all",
                                                        fontSize: "13px"
                                                    }}
                                                >
                                                    {showSecret &&
                                                    selectedWebhook.secretKey
                                                        ? selectedWebhook.secretKey
                                                        : "••••••••••••••••••••••••"}
                                                </Typography>
                                                <Tooltip
                                                    title={
                                                        showSecret
                                                            ? "Hide"
                                                            : "Reveal"
                                                    }
                                                >
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            setShowSecret(
                                                                !showSecret
                                                            )
                                                        }
                                                    >
                                                        {showSecret ? (
                                                            <VisibilityOffIcon
                                                                sx={{
                                                                    fontSize: 16
                                                                }}
                                                            />
                                                        ) : (
                                                            <VisibilityIcon
                                                                sx={{
                                                                    fontSize: 16
                                                                }}
                                                            />
                                                        )}
                                                    </IconButton>
                                                </Tooltip>
                                                {selectedWebhook.secretKey && (
                                                    <Tooltip title="Copy Secret">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                handleCopy(
                                                                    selectedWebhook.secretKey,
                                                                    "Secret Key"
                                                                )
                                                            }
                                                        >
                                                            <ContentCopyIcon
                                                                sx={{
                                                                    fontSize: 16
                                                                }}
                                                            />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </Box> */}

                                        {/* Headers */}
                                        {selectedWebhook.headers &&
                                            Object.keys(selectedWebhook.headers)
                                                .length > 0 && (
                                                <Box>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{
                                                            textTransform:
                                                                "uppercase",
                                                            letterSpacing:
                                                                "0.5px",
                                                            fontWeight: 500,
                                                            display: "block",
                                                            mb: 0.75
                                                        }}
                                                    >
                                                        Custom Headers
                                                    </Typography>
                                                    <Box
                                                        sx={{
                                                            backgroundColor:
                                                                grey[50],
                                                            borderRadius: 1,
                                                            p: 1.5
                                                        }}
                                                    >
                                                        {Object.entries(
                                                            selectedWebhook.headers
                                                        ).map(
                                                            (
                                                                [key, value],
                                                                idx,
                                                                arr
                                                            ) => (
                                                                <Box
                                                                    key={key}
                                                                    sx={{
                                                                        display:
                                                                            "flex",
                                                                        gap: 1,
                                                                        mb:
                                                                            idx <
                                                                            arr.length -
                                                                                1
                                                                                ? 1
                                                                                : 0
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            fontFamily:
                                                                                "monospace",
                                                                            fontSize:
                                                                                "13px",
                                                                            color: theme
                                                                                .palette
                                                                                .text
                                                                                .secondary,
                                                                            fontWeight: 500
                                                                        }}
                                                                    >
                                                                        {key}:
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            fontFamily:
                                                                                "monospace",
                                                                            fontSize:
                                                                                "13px",
                                                                            wordBreak:
                                                                                "break-all"
                                                                        }}
                                                                    >
                                                                        {value}
                                                                    </Typography>
                                                                </Box>
                                                            )
                                                        )}
                                                    </Box>
                                                </Box>
                                            )}

                                        {/* Signature */}
                                        <Box
                                            sx={{
                                                mt:
                                                    selectedWebhook.headers &&
                                                    Object.keys(
                                                        selectedWebhook.headers
                                                    ).length > 0
                                                        ? 2.5
                                                        : 0
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.5px",
                                                    fontWeight: 500,
                                                    display: "block",
                                                    mb: 0.75
                                                }}
                                            >
                                                Signature
                                            </Typography>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1,
                                                    backgroundColor: grey[50],
                                                    borderRadius: 1,
                                                    px: 1.5,
                                                    py: 1
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontFamily: "monospace",
                                                        flex: 1,
                                                        wordBreak: "break-all",
                                                        fontSize: "13px",
                                                        color: showSecret
                                                            ? "inherit"
                                                            : grey[500]
                                                    }}
                                                >
                                                    {showSecret && secretKey
                                                        ? secretKey
                                                        : "••••••••••••••••••••••••••••••••"}
                                                </Typography>
                                                <Tooltip
                                                    title={
                                                        showSecret
                                                            ? "Hide"
                                                            : "Reveal"
                                                    }
                                                >
                                                    <IconButton
                                                        size="small"
                                                        onClick={
                                                            handleRevealSignature
                                                        }
                                                        disabled={
                                                            isLoadingSecret
                                                        }
                                                    >
                                                        {isLoadingSecret ? (
                                                            <CircularProgress
                                                                size={16}
                                                            />
                                                        ) : showSecret ? (
                                                            <VisibilityOffIcon
                                                                sx={{
                                                                    fontSize: 16
                                                                }}
                                                            />
                                                        ) : (
                                                            <VisibilityIcon
                                                                sx={{
                                                                    fontSize: 16
                                                                }}
                                                            />
                                                        )}
                                                    </IconButton>
                                                </Tooltip>
                                                {showSecret && secretKey && (
                                                    <Tooltip title="Copy Signature">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                handleCopy(
                                                                    secretKey,
                                                                    "Signature"
                                                                )
                                                            }
                                                        >
                                                            <ContentCopyIcon
                                                                sx={{
                                                                    fontSize: 16
                                                                }}
                                                            />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title="Rotate Signature">
                                                    <Button
                                                        size="small"
                                                        variant="text"
                                                        onClick={
                                                            handleRotateSignature
                                                        }
                                                        disabled={
                                                            isRotatingSignature
                                                        }
                                                        sx={{
                                                            minWidth: "auto",
                                                            textTransform:
                                                                "none",
                                                            fontSize: "12px",
                                                            color: "text.secondary",
                                                            "&:hover": {
                                                                color: "primary.main"
                                                            }
                                                        }}
                                                    >
                                                        {isRotatingSignature
                                                            ? "Rotating..."
                                                            : "Rotate"}
                                                    </Button>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Logs Section */}
                            <Grid item xs={12}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        mb: 1.5,
                                        mt: 1
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        sx={{ fontSize: "1rem" }}
                                    >
                                        Delivery logs
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            gap: 1,
                                            alignItems: "center"
                                        }}
                                    >
                                        {/* Send Test Event Button */}
                                        <Button
                                            variant="outlined"
                                            onClick={handleSendTestEvent}
                                            disabled={
                                                isTesting || testCooldown > 0
                                            }
                                            startIcon={
                                                isTesting ? (
                                                    <CircularProgress
                                                        size={14}
                                                        color="inherit"
                                                    />
                                                ) : (
                                                    <SendIcon
                                                        sx={{ fontSize: 16 }}
                                                    />
                                                )
                                            }
                                            sx={{
                                                textTransform: "none",
                                                height: "40px",
                                                minWidth: "140px"
                                            }}
                                        >
                                            {isTesting
                                                ? "Sending..."
                                                : testCooldown > 0
                                                  ? `Wait ${testCooldown}s`
                                                  : "Send test event"}
                                        </Button>

                                        <FormControl
                                            size="small"
                                            sx={{ minWidth: 130 }}
                                        >
                                            <InputLabel
                                                id="time-filter-label"
                                                sx={{
                                                    "&.MuiInputLabel-shrink": {
                                                        transform:
                                                            "translate(14px, -6px) scale(0.75)"
                                                    }
                                                }}
                                            >
                                                Time range
                                            </InputLabel>
                                            <Select
                                                labelId="time-filter-label"
                                                value={timeFilter}
                                                label="Time range"
                                                onChange={
                                                    handleTimeFilterChange
                                                }
                                                disabled={
                                                    isLoadingLogs || isFetching
                                                }
                                                sx={{
                                                    "& fieldset": {
                                                        "& legend": {
                                                            width: "68px"
                                                        }
                                                    }
                                                }}
                                            >
                                                {TIME_FILTER_OPTIONS.map(
                                                    (option) => (
                                                        <MenuItem
                                                            key={option.value}
                                                            value={option.value}
                                                        >
                                                            {option.label}
                                                        </MenuItem>
                                                    )
                                                )}
                                            </Select>
                                        </FormControl>
                                        <Tooltip title="Refresh logs">
                                            <IconButton
                                                size="small"
                                                onClick={handleRefreshLogs}
                                                disabled={
                                                    isLoadingLogs || isFetching
                                                }
                                            >
                                                <RefreshIcon
                                                    sx={{
                                                        fontSize: 20,
                                                        animation: isFetching
                                                            ? "spin 1s linear infinite"
                                                            : "none",
                                                        "@keyframes spin": {
                                                            "0%": {
                                                                transform:
                                                                    "rotate(0deg)"
                                                            },
                                                            "100%": {
                                                                transform:
                                                                    "rotate(360deg)"
                                                            }
                                                        }
                                                    }}
                                                />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>

                                {isLoadingLogs && logs.length === 0 ? (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            py: 4
                                        }}
                                    >
                                        <CircularProgress size={28} />
                                    </Box>
                                ) : logs && logs.length > 0 ? (
                                    <>
                                        <TableContainer
                                            component={Paper}
                                            variant="outlined"
                                            sx={{
                                                borderColor: grey[300]
                                            }}
                                        >
                                            <Table size="small" stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell
                                                            sx={{ width: 50 }}
                                                        >
                                                            Status
                                                        </TableCell>
                                                        <TableCell>
                                                            Timestamp
                                                        </TableCell>
                                                        <TableCell>
                                                            Event
                                                        </TableCell>
                                                        <TableCell>
                                                            HTTP
                                                        </TableCell>
                                                        <TableCell>
                                                            Duration
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {paginatedLogs.map(
                                                        (log) => (
                                                            <TableRow
                                                                key={log.id}
                                                                hover
                                                                onClick={() =>
                                                                    handleSelectLog(
                                                                        log
                                                                    )
                                                                }
                                                                selected={
                                                                    selectedLog?.id ===
                                                                    log.id
                                                                }
                                                                sx={{
                                                                    cursor: "pointer",
                                                                    "&.Mui-selected":
                                                                        {
                                                                            backgroundColor:
                                                                                alpha(
                                                                                    theme
                                                                                        .palette
                                                                                        .primary
                                                                                        .main,
                                                                                    0.08
                                                                                )
                                                                        },
                                                                    "&.Mui-selected:hover":
                                                                        {
                                                                            backgroundColor:
                                                                                alpha(
                                                                                    theme
                                                                                        .palette
                                                                                        .primary
                                                                                        .main,
                                                                                    0.12
                                                                                )
                                                                        }
                                                                }}
                                                            >
                                                                <TableCell>
                                                                    {log.status ===
                                                                    "success" ? (
                                                                        <CheckCircleIcon
                                                                            sx={{
                                                                                color: theme
                                                                                    .palette
                                                                                    .success
                                                                                    .main,
                                                                                fontSize: 18
                                                                            }}
                                                                        />
                                                                    ) : (
                                                                        <ErrorIcon
                                                                            sx={{
                                                                                color: theme
                                                                                    .palette
                                                                                    .error
                                                                                    .main,
                                                                                fontSize: 18
                                                                            }}
                                                                        />
                                                                    )}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Tooltip
                                                                        title={moment(
                                                                            log.createdAt
                                                                        ).format(
                                                                            "MMMM D, YYYY h:mm:ss A"
                                                                        )}
                                                                    >
                                                                        <Typography
                                                                            variant="body2"
                                                                            sx={{
                                                                                fontSize:
                                                                                    "13px"
                                                                            }}
                                                                        >
                                                                            {moment(
                                                                                log.createdAt
                                                                            ).format(
                                                                                "MMM D, h:mm:ss A"
                                                                            )}
                                                                        </Typography>
                                                                    </Tooltip>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            fontFamily:
                                                                                "monospace",
                                                                            fontSize:
                                                                                "12px",
                                                                            color: grey[600]
                                                                        }}
                                                                    >
                                                                        {
                                                                            log.eventType
                                                                        }
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Chip
                                                                        label={
                                                                            log.httpStatus ||
                                                                            "N/A"
                                                                        }
                                                                        size="small"
                                                                        sx={{
                                                                            height: 20,
                                                                            fontSize:
                                                                                "11px",
                                                                            fontWeight: 500,
                                                                            backgroundColor:
                                                                                log.httpStatus >=
                                                                                    200 &&
                                                                                log.httpStatus <
                                                                                    300
                                                                                    ? alpha(
                                                                                          theme
                                                                                              .palette
                                                                                              .success
                                                                                              .main,
                                                                                          0.1
                                                                                      )
                                                                                    : alpha(
                                                                                          theme
                                                                                              .palette
                                                                                              .error
                                                                                              .main,
                                                                                          0.1
                                                                                      ),
                                                                            color:
                                                                                log.httpStatus >=
                                                                                    200 &&
                                                                                log.httpStatus <
                                                                                    300
                                                                                    ? theme
                                                                                          .palette
                                                                                          .success
                                                                                          .dark
                                                                                    : theme
                                                                                          .palette
                                                                                          .error
                                                                                          .dark
                                                                        }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            fontSize:
                                                                                "13px",
                                                                            color: grey[600]
                                                                        }}
                                                                    >
                                                                        {log.responseTimeMs
                                                                            ? `${log.responseTimeMs}ms`
                                                                            : "-"}
                                                                    </Typography>
                                                                </TableCell>
                                                            </TableRow>
                                                        )
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>

                                        {/* Pagination */}
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center"
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ pl: 1 }}
                                            >
                                                {!hasSelectedLog
                                                    ? "Click a row to view details"
                                                    : ""}
                                            </Typography>
                                            <TablePagination
                                                component="div"
                                                count={
                                                    nextToken ? -1 : logs.length
                                                }
                                                page={logsPage}
                                                onPageChange={
                                                    handleChangeLogsPage
                                                }
                                                rowsPerPage={logsRowsPerPage}
                                                onRowsPerPageChange={
                                                    handleChangeLogsRowsPerPage
                                                }
                                                rowsPerPageOptions={[5, 10, 25]}
                                                labelDisplayedRows={({
                                                    from,
                                                    to,
                                                    count
                                                }) =>
                                                    count !== -1
                                                        ? `${from}-${to} of ${count}`
                                                        : `${from}-${to} of ${logs.length}+`
                                                }
                                                sx={{
                                                    "& .MuiTablePagination-toolbar":
                                                        {
                                                            minHeight: 40
                                                        },
                                                    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                                                        {
                                                            fontSize: "12px"
                                                        }
                                                }}
                                            />
                                        </Box>
                                    </>
                                ) : (
                                    <Box
                                        sx={{
                                            textAlign: "center",
                                            py: 4,
                                            backgroundColor: grey[50],
                                            borderRadius: 1
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            No delivery logs for the selected
                                            time range
                                        </Typography>
                                    </Box>
                                )}
                            </Grid>
                        </Grid>

                        {/* Created Date - Bottom Right */}
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                mt: 3,
                                pt: 2,
                                borderTop: `1px solid ${grey[200]}`
                            }}
                        >
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontStyle: "italic" }}
                            >
                                Created{" "}
                                {moment(selectedWebhook.createdAt).format(
                                    "MMMM D, YYYY"
                                )}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Log Detail Panel */}
                    {hasSelectedLog && (
                        <Box
                            sx={{
                                flex: "0 0 43%",
                                overflow: "auto",
                                display: "flex",
                                flexDirection: "column",
                                borderLeft: `1px solid ${grey[200]}`,
                                pl: 3
                            }}
                        >
                            <LogDetailPanel
                                log={selectedLog}
                                onClose={() => setSelectedLog(null)}
                                handleCopy={handleCopy}
                                onRetry={handleRetryDelivery}
                                isRetrying={isRetrying}
                                retryCooldown={retryCooldown}
                            />
                        </Box>
                    )}
                </Box>
            </DialogContent>

            <DialogActions
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "18px 36px",
                    borderTop: `1px solid ${grey[200]}`
                }}
            >
                {/* Delete Webhook Button - Styled like Delete user */}
                <Button
                    variant="text"
                    color="error"
                    onClick={handleDelete}
                    disabled={isDeleting}
                    disableRipple
                    sx={{
                        "&:hover": {
                            backgroundColor: "transparent !important"
                        }
                    }}
                >
                    <Typography
                        variant="button"
                        sx={{
                            textTransform: "none",
                            lineHeight: "16px"
                        }}
                    >
                        {isDeleting ? "Deleting..." : "Delete webhook"}
                    </Typography>
                </Button>

                {isDesktop && (
                    <Button
                        onClick={handleClose}
                        variant="outlined"
                        sx={{ height: "40px", minWidth: "90px" }}
                    >
                        Close
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

// Log Detail Panel Component
const LogDetailPanel = ({
    log,
    onClose,
    handleCopy,
    onRetry,
    isRetrying,
    retryCooldown
}) => {
    const theme = useTheme();

    const formatPayload = (payload) => {
        if (!payload) return null;
        try {
            if (typeof payload === "object") {
                return JSON.stringify(payload, null, 2);
            }
            const parsed = JSON.parse(payload);
            return JSON.stringify(parsed, null, 2);
        } catch {
            return typeof payload === "string"
                ? payload
                : JSON.stringify(payload, null, 2);
        }
    };

    const formattedPayload = formatPayload(log.requestPayload);

    return (
        <Box
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column"
            }}
        >
            {/* Panel Header */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2
                }}
            >
                <Typography variant="h6" sx={{ fontSize: "1rem" }}>
                    Log details
                </Typography>
                <IconButton size="small" onClick={onClose}>
                    <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Box>

            {/* Status Banner */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 2.5,
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor:
                        log.status === "success"
                            ? alpha(theme.palette.success.main, 0.08)
                            : alpha(theme.palette.error.main, 0.08)
                }}
            >
                {log.status === "success" ? (
                    <CheckCircleIcon
                        sx={{ color: theme.palette.success.main, fontSize: 22 }}
                    />
                ) : (
                    <ErrorIcon
                        sx={{ color: theme.palette.error.main, fontSize: 22 }}
                    />
                )}
                <Box>
                    <Typography variant="body2" fontWeight={500}>
                        {log.status === "success"
                            ? "Delivered successfully"
                            : "Delivery failed"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {moment(log.createdAt).format(
                            "MMMM D, YYYY [at] h:mm:ss A"
                        )}
                    </Typography>
                </Box>
            </Box>

            {/* Retry Button - Only show for failed deliveries */}
            {log.status !== "success" && log.requestPayload && (
                <Box sx={{ mb: 2 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => onRetry(log)}
                        disabled={isRetrying || retryCooldown > 0}
                        sx={{
                            textTransform: "none",
                            minWidth: "100px"
                        }}
                    >
                        {isRetrying
                            ? "Retrying..."
                            : retryCooldown > 0
                              ? `Retry (${retryCooldown}s)`
                              : "Retry delivery"}
                    </Button>
                </Box>
            )}

            {/* Metadata Grid */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            fontWeight: 500,
                            display: "block",
                            mb: 0.5
                        }}
                    >
                        HTTP Status
                    </Typography>
                    <Chip
                        label={log.httpStatus || "N/A"}
                        size="small"
                        sx={{
                            backgroundColor:
                                log.httpStatus >= 200 && log.httpStatus < 300
                                    ? alpha(theme.palette.success.main, 0.1)
                                    : alpha(theme.palette.error.main, 0.1),
                            color:
                                log.httpStatus >= 200 && log.httpStatus < 300
                                    ? theme.palette.success.dark
                                    : theme.palette.error.dark,
                            fontWeight: 500
                        }}
                    />
                </Grid>
                <Grid item xs={6}>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            fontWeight: 500,
                            display: "block",
                            mb: 0.5
                        }}
                    >
                        Response Time
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                        {log.responseTimeMs ? `${log.responseTimeMs}ms` : "N/A"}
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            fontWeight: 500,
                            display: "block",
                            mb: 0.5
                        }}
                    >
                        Event Type
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace", fontSize: "12px" }}
                    >
                        {log.eventType}
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            fontWeight: 500,
                            display: "block",
                            mb: 0.5
                        }}
                    >
                        Event ID
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            fontFamily: "monospace",
                            fontSize: "11px",
                            wordBreak: "break-all"
                        }}
                    >
                        {log.eventId || "N/A"}
                    </Typography>
                </Grid>
            </Grid>

            {/* Log ID */}
            <Box sx={{ mb: 2 }}>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        fontWeight: 500,
                        display: "block",
                        mb: 0.5
                    }}
                >
                    Log ID
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        fontFamily: "monospace",
                        fontSize: "11px",
                        wordBreak: "break-all",
                        color: grey[600]
                    }}
                >
                    {log.id}
                </Typography>
            </Box>

            {/* Error Message */}
            {log.errorMessage && (
                <Box sx={{ mb: 2 }}>
                    <Typography
                        variant="caption"
                        sx={{
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            fontWeight: 500,
                            display: "block",
                            mb: 0.5,
                            color: theme.palette.error.main
                        }}
                    >
                        Error Message
                    </Typography>
                    <Box
                        sx={{
                            p: 1.5,
                            backgroundColor: alpha(
                                theme.palette.error.main,
                                0.08
                            ),
                            borderRadius: 1,
                            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{ fontFamily: "monospace", fontSize: "12px" }}
                        >
                            {log.errorMessage}
                        </Typography>
                    </Box>
                </Box>
            )}

            {/* Request Payload */}
            {formattedPayload && (
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 0.75
                        }}
                    >
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                fontWeight: 500
                            }}
                        >
                            Request Payload
                        </Typography>
                        <Tooltip title="Copy Payload">
                            <IconButton
                                size="small"
                                onClick={() =>
                                    handleCopy(formattedPayload, "Payload")
                                }
                            >
                                <ContentCopyIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Box
                        sx={{
                            p: 1.5,
                            backgroundColor: grey[50],
                            borderRadius: 1,
                            border: `1px solid ${grey[200]}`,
                            flex: 1,
                            overflow: "auto",
                            minHeight: 0
                        }}
                    >
                        <pre
                            style={{
                                margin: 0,
                                fontFamily: "monospace",
                                fontSize: "12px",
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                lineHeight: 1.5
                            }}
                        >
                            {formattedPayload}
                        </pre>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default ViewWebhookModal;
